import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";
import { ViewExtrasSlot } from "../../../../common/components/projectSlots";

import { AuthorComposerViewStore } from "../../store/AuthorComposerViewStore";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { FieldAdornmentSlot } from "../../../../common/components/projectSlots";
import { Gender } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { TaxIdType } from "../../../../api/graphql";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { ShowReference } from "../../../common/controls";
import { ShowDate } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AuthorComposerPublicViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  recordId?: string;
  firstNameValue?: { displayId: string; id: string } | null;
  firstName: string;
  lastNameValue?: { displayId: string; id: string } | null;
  lastName: string;
  genderOptions: { id: string; displayId: string }[];
  gender: Gender | "";
  taxIdTypeOptions: { id: string; displayId: string }[];
  taxIdType: TaxIdType | "";
  taxIdValue?: { displayId: string; id: string } | null;
  taxId: string;
  emailValue?: { displayId: string; id: string } | null;
  email: string;
  phoneValue?: { displayId: string; id: string } | null;
  phone: string;
  nationalityValue: { displayId: string; id: string } | null;
  dateOfBirthValue?: { displayId: string; id: string } | null;
  dateOfBirth: string | null;
  streetAndNumberValue?: { displayId: string; id: string } | null;
  streetAndNumber: string;
  floorValue?: { displayId: string; id: string } | null;
  floor: string;
  suiteValue?: { displayId: string; id: string } | null;
  suite: string;
  postalCodeValue?: { displayId: string; id: string } | null;
  postalCode: string;
  countryValue: { displayId: string; id: string } | null;
  provinceValue: { displayId: string; id: string } | null;
  cityValue?: { displayId: string; id: string } | null;
  city: string;
  akaValue?: { displayId: string; id: string } | null;
  aka: string;
  ipiValue?: { displayId: string; id: string } | null;
  ipi: string;
  authorComposerViewStore: AuthorComposerViewStore;
};

export const AuthorComposerPublicViewForm: React.FC<AuthorComposerPublicViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      recordId,
      firstName = "",
      lastName = "",
      genderOptions = [],
      gender = "" as Gender | "",
      taxIdTypeOptions = [],
      taxIdType = "" as TaxIdType | "",
      taxId = "",
      email = "",
      phone = "",
      nationalityValue,
      dateOfBirth = null,
      streetAndNumber = "",
      floor = "",
      suite = "",
      postalCode = "",
      countryValue,
      provinceValue,
      city = "",
      aka = "",
      ipi = "",
      authorComposerViewStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <ViewExtrasSlot entity="AuthorComposer" recordId={recordId} />
            <GroupContainer
              id="personalInformation"
              title={
                langStore.groupAuthorComposerPublicPersonalInformationLabel
              }
              collapsible={true}
              expanded={authorComposerViewStore.getCardState(
                "personalInformation"
              )}
              toggleExpand={() =>
                authorComposerViewStore.toggleCardState("personalInformation")
              }
            >
              <FormControl fullWidth>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="firstName"
                  mode="view"
                  recordId={recordId}
                  value={firstName}
                >
                  <ShowMultiLine
                    value={firstName}
                    label={langStore.AuthorComposerFirstNameLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="lastName"
                  mode="view"
                  recordId={recordId}
                  value={lastName}
                >
                  <ShowMultiLine
                    value={lastName}
                    label={langStore.AuthorComposerLastNameLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="gender"
                  mode="view"
                  recordId={recordId}
                  value={gender}
                >
                  <ShowString
                    value={
                      gender
                        ? genderOptions.find(
                            (o: { id: string }) => o.id === gender
                          )?.displayId || gender
                        : ""
                    }
                    label={langStore.AuthorComposerGenderLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="taxIdType"
                  mode="view"
                  recordId={recordId}
                  value={taxIdType}
                >
                  <ShowString
                    value={
                      taxIdType
                        ? taxIdTypeOptions.find(
                            (o: { id: string }) => o.id === taxIdType
                          )?.displayId || taxIdType
                        : ""
                    }
                    label={langStore.AuthorComposerTaxIdTypeLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="taxId"
                  mode="view"
                  recordId={recordId}
                  value={taxId}
                >
                  <ShowMultiLine
                    value={taxId}
                    label={langStore.AuthorComposerTaxIdLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="email"
                  mode="view"
                  recordId={recordId}
                  value={email}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <ShowString
                      value={email}
                      label={langStore.AuthorComposerEmailLabel}
                    />
                    <EmailVerificationIndicator
                      email={email}
                      entityName="AuthorComposer"
                      recordId={recordId}
                      validationMode={true}
                    />
                  </Box>
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="phone"
                  mode="view"
                  recordId={recordId}
                  value={phone}
                >
                  <ShowMultiLine
                    value={phone}
                    label={langStore.AuthorComposerPhoneLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="nationality"
                  mode="view"
                  recordId={recordId}
                  value={nationalityValue}
                >
                  <ShowReference
                    label={langStore.AuthorComposerNationalityLabel}
                    selectedValue={langStore.displayIdByLang(
                      nationalityValue?.displayId
                    )}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="dateOfBirth"
                  mode="view"
                  recordId={recordId}
                  value={dateOfBirth}
                >
                  <ShowDate
                    value={dateOfBirth}
                    label={langStore.AuthorComposerDateOfBirthLabel}
                    locale={langStore.timeFormat}
                  />
                </FieldAdornmentSlot>
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="address"
              title={langStore.groupAuthorComposerPublicAddressLabel}
              collapsible={true}
              expanded={authorComposerViewStore.getCardState("address")}
              toggleExpand={() =>
                authorComposerViewStore.toggleCardState("address")
              }
            >
              <FormControl fullWidth>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="streetAndNumber"
                  mode="view"
                  recordId={recordId}
                  value={streetAndNumber}
                >
                  <ShowMultiLine
                    value={streetAndNumber}
                    label={langStore.AuthorComposerStreetAndNumberLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="floor"
                  mode="view"
                  recordId={recordId}
                  value={floor}
                >
                  <ShowMultiLine
                    value={floor}
                    label={langStore.AuthorComposerFloorLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="suite"
                  mode="view"
                  recordId={recordId}
                  value={suite}
                >
                  <ShowMultiLine
                    value={suite}
                    label={langStore.AuthorComposerSuiteLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="postalCode"
                  mode="view"
                  recordId={recordId}
                  value={postalCode}
                >
                  <ShowMultiLine
                    value={postalCode}
                    label={langStore.AuthorComposerPostalCodeLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="country"
                  mode="view"
                  recordId={recordId}
                  value={countryValue}
                >
                  <ShowReference
                    label={langStore.AuthorComposerCountryLabel}
                    selectedValue={langStore.displayIdByLang(
                      countryValue?.displayId
                    )}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="province"
                  mode="view"
                  recordId={recordId}
                  value={provinceValue}
                >
                  <ShowReference
                    label={langStore.AuthorComposerProvinceLabel}
                    selectedValue={langStore.displayIdByLang(
                      provinceValue?.displayId
                    )}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="city"
                  mode="view"
                  recordId={recordId}
                  value={city}
                >
                  <ShowMultiLine
                    value={city}
                    label={langStore.AuthorComposerCityLabel}
                  />
                </FieldAdornmentSlot>
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="professionalInformation"
              title={
                langStore.groupAuthorComposerPublicProfessionalInformationLabel
              }
              collapsible={true}
              expanded={authorComposerViewStore.getCardState(
                "professionalInformation"
              )}
              toggleExpand={() =>
                authorComposerViewStore.toggleCardState(
                  "professionalInformation"
                )
              }
            >
              <FormControl fullWidth>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="aka"
                  mode="view"
                  recordId={recordId}
                  value={aka}
                >
                  <ShowMultiLine
                    value={aka}
                    label={langStore.AuthorComposerAkaLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="ipi"
                  mode="view"
                  recordId={recordId}
                  value={ipi}
                >
                  <ShowMultiLine
                    value={ipi}
                    label={langStore.AuthorComposerIpiLabel}
                  />
                </FieldAdornmentSlot>
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
